import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MapPin, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { DealList } from '../components/DealBits';
import { ContactPicker, OrgPicker } from '../components/Pickers';
import { TenureSelect } from '../components/Tenure';
import { Button, Card, Empty, ErrorBox, Field, Input, Modal, PageHeader, Pill, Spinner } from '../components/ui';
import { date, personName, PROPERTY_TYPE_LABEL } from '../lib/format';
import { supabase, unwrap } from '../lib/supabase';
import { BuildingForm } from './Buildings';

function useBuilding(id: string) {
  return useQuery({
    queryKey: ['building', id],
    queryFn: async () => {
      const [b, suites, occupants, owners, deals, matches] = await Promise.all([
        supabase.from('buildings').select('*').eq('id', id).single(),
        supabase.from('suites').select('id, suite_number, sf, notes').eq('building_id', id).order('suite_number'),
        supabase.from('org_locations').select('id, tenure, location_type, lease_expiration, organizations(id, name), suites(suite_number)').eq('building_id', id),
        supabase.from('building_ownership').select('id, contacts(id, first_name, last_name, display_name), organizations(id, name)').eq('building_id', id),
        supabase.from('deals').select('id, name, deal_type, stage, expected_close_date, next_step').eq('building_id', id),
        supabase.from('building_requirement_matches').select('requirement_id, org_id, size_known').eq('building_id', id),
      ]);
      const m = unwrap(matches);
      const reqs = m.length
        ? unwrap(await supabase.from('tenant_requirements').select('id, sf_min, sf_max, timing, target_markets, organizations(id, name)').in('id', m.map((x) => x.requirement_id!)))
        : [];
      return { b: unwrap(b), suites: unwrap(suites), occupants: unwrap(occupants), owners: unwrap(owners), deals: unwrap(deals), reqs, matches: m };
    },
  });
}

export function BuildingDetail() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const { data, isLoading, error } = useBuilding(id);
  const [editing, setEditing] = useState(false);
  const [addingOwner, setAddingOwner] = useState(false);
  const [suite, setSuite] = useState({ number: '', sf: '' });
  const refresh = () => qc.invalidateQueries({ queryKey: ['building', id] });

  const addSuite = useMutation({
    mutationFn: async () =>
      unwrap(await supabase.from('suites').insert({ building_id: id, suite_number: suite.number.trim(), sf: suite.sf ? Number(suite.sf) : null })),
    onSuccess: () => {
      setSuite({ number: '', sf: '' });
      refresh();
    },
  });
  const removeSuite = useMutation({
    mutationFn: async (suiteId: string) => unwrap(await supabase.from('suites').delete().eq('id', suiteId)),
    onSuccess: refresh,
  });
  const addOwner = useMutation({
    mutationFn: async (o: { contactId?: string; orgId?: string }) =>
      unwrap(await supabase.from('building_ownership').insert({ building_id: id, owner_contact_id: o.contactId ?? null, owner_org_id: o.orgId ?? null, source: 'manual' })),
    onSuccess: () => {
      setAddingOwner(false);
      refresh();
    },
  });
  const removeOwner = useMutation({
    mutationFn: async (ownershipId: string) => unwrap(await supabase.from('building_ownership').delete().eq('id', ownershipId)),
    onSuccess: refresh,
  });

  if (isLoading) return <Spinner />;
  if (error || !data) return <ErrorBox error={error ?? 'Building not found'} />;
  const { b } = data;
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([b.street, b.city, b.state, b.zip5].filter(Boolean).join(', ') || b.address_raw || '')}`;

  return (
    <div>
      <PageHeader
        title={b.name || b.street || b.address_raw || 'Building'}
        subtitle={
          <div className="flex flex-wrap items-center gap-2">
            <span>{[b.name ? b.street : null, b.city, b.state, b.zip5].filter(Boolean).join(', ')}</span>
            {b.property_type && <Pill>{PROPERTY_TYPE_LABEL[b.property_type]}</Pill>}
            <a href={mapsUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-indigo-600 hover:underline">
              <MapPin className="h-3 w-3" /> Map
            </a>
          </div>
        }
        actions={
          <Button onClick={() => setEditing(true)}>
            <Pencil className="h-4 w-4" /> Edit
          </Button>
        }
      />
      {b.address_needs_review && (
        <div className="mb-4 rounded-md border border-orange-200 bg-orange-50 px-3 py-2 text-sm text-orange-900">
          This address looks incomplete{b.address_raw ? `: “${b.address_raw}”` : ''}. Use Edit to fix it.
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4">
          <Card title="Owners" actions={<Button size="sm" variant="ghost" onClick={() => setAddingOwner(true)}>+ Add</Button>}>
            {data.owners.length === 0 ? (
              <Empty>No owner on file.</Empty>
            ) : (
              <ul className="space-y-1.5 text-sm">
                {data.owners.map((o) => (
                  <li key={o.id} className="group flex items-center gap-2">
                    <div className="flex-1">
                      {o.contacts && (
                        <Link to={`/contacts/${o.contacts.id}`} className="font-medium hover:text-indigo-700">
                          {personName(o.contacts)}
                        </Link>
                      )}
                      {o.contacts && o.organizations && <span className="text-slate-400"> · </span>}
                      {o.organizations && (
                        <Link to={`/companies/${o.organizations.id}`} className="hover:text-indigo-700">
                          {o.organizations.name}
                        </Link>
                      )}
                    </div>
                    <button className="hidden text-slate-300 hover:text-rose-600 group-hover:block" onClick={() => removeOwner.mutate(o.id)} aria-label="Remove owner">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Card>
          <Card title="Suites">
            {data.suites.length === 0 ? (
              <Empty>No suites recorded.</Empty>
            ) : (
              <ul className="mb-3 space-y-1 text-sm">
                {data.suites.map((s) => (
                  <li key={s.id} className="group flex items-center gap-2">
                    <span className="font-medium">{s.suite_number}</span>
                    {s.sf && <span className="text-slate-500">{s.sf.toLocaleString()} SF</span>}
                    <button className="ml-auto hidden text-slate-300 hover:text-rose-600 group-hover:block" onClick={() => removeSuite.mutate(s.id)} aria-label="Remove suite">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex gap-2">
              <Input value={suite.number} onChange={(e) => setSuite({ ...suite, number: e.target.value })} placeholder="Suite" />
              <Input value={suite.sf} onChange={(e) => setSuite({ ...suite, sf: e.target.value })} placeholder="SF" type="number" />
              <Button onClick={() => addSuite.mutate()} disabled={!suite.number.trim()} busy={addSuite.isPending}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <ErrorBox error={addSuite.error ?? removeSuite.error} />
          </Card>
          {b.notes && (
            <Card title="Notes">
              <p className="whitespace-pre-wrap text-sm text-slate-600">{b.notes}</p>
            </Card>
          )}
        </div>

        <div className="space-y-4 lg:col-span-2">
          <Card title={`Occupants (${data.occupants.length})`}>
            {data.occupants.length === 0 ? (
              <Empty>No known occupants.</Empty>
            ) : (
              <ul className="divide-y divide-slate-100 text-sm">
                {data.occupants.map((o) => (
                  <li key={o.id} className="flex flex-wrap items-center gap-2 py-1.5">
                    {o.organizations && (
                      <Link to={`/companies/${o.organizations.id}`} className="font-medium hover:text-indigo-700">
                        {o.organizations.name}
                      </Link>
                    )}
                    {o.suites?.suite_number && <span className="text-slate-500">{o.suites.suite_number}</span>}
                    {o.location_type && <span className="text-xs text-slate-400">{o.location_type}</span>}
                    {o.lease_expiration && <span className="text-xs text-slate-500">exp. {date(o.lease_expiration)}</span>}
                    <span className="ml-auto">
                      <TenureSelect locationId={o.id} tenure={o.tenure} invalidate={[['building', id]]} />
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
          <Card title="Tenant requirements this might fit">
            {data.reqs.length === 0 ? (
              <Empty>No active requirement matches this building's city{data.suites.some((s) => s.sf) ? ' and suite sizes' : ''}.</Empty>
            ) : (
              <ul className="space-y-1.5 text-sm">
                {data.reqs.map((r) => (
                  <li key={r.id}>
                    {r.organizations && (
                      <Link to={`/companies/${r.organizations.id}`} className="font-medium hover:text-indigo-700">
                        {r.organizations.name}
                      </Link>
                    )}
                    <span className="text-slate-500">
                      {' '}
                      · {r.target_markets.join(', ')}
                      {(r.sf_min || r.sf_max) && ` · ${r.sf_min?.toLocaleString() ?? '?'}–${r.sf_max?.toLocaleString() ?? '?'} SF`}
                      {r.timing && ` · ${r.timing}`}
                    </span>
                    {data.matches.find((m) => m.requirement_id === r.id)?.size_known === false && <Pill className="ml-2">size not checked</Pill>}
                  </li>
                ))}
              </ul>
            )}
          </Card>
          <Card title="Deals">
            <DealList deals={data.deals} />
          </Card>
        </div>
      </div>

      <Modal title="Edit building" open={editing} onClose={() => setEditing(false)}>
        <BuildingForm
          building={b}
          onDone={() => {
            setEditing(false);
            refresh();
          }}
        />
      </Modal>
      <Modal title="Add an owner" open={addingOwner} onClose={() => setAddingOwner(false)}>
        <div className="space-y-4">
          <Field label="A person">
            <ContactPicker onPick={(o) => addOwner.mutate({ contactId: o.id })} />
          </Field>
          <Field label="…or a company">
            <OrgPicker onPick={(o) => addOwner.mutate({ orgId: o.id })} />
          </Field>
          <ErrorBox error={addOwner.error} />
        </div>
      </Modal>
    </div>
  );
}
